import React, { useCallback, useEffect, useState } from "react";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "../../libs/@fullcalendar/common";
import { Box, Button, HStack, useDisclosure } from "@chakra-ui/react";
import { TrialBanner } from "../../components/TrialBanner";
import { Activity, Reservation, Planning, PlanningType } from "../../types";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { CalendarNavButtons } from "../../components/CalendarNavButtons";
import { ReservationModal } from "../../components/ReservationModal";
import { SubscriptionBanner } from "../../components/SubscriptionBanner";
import { PlanningModal } from "../../components/PlanningModal";
import { datesAreOnSameDay } from "../../utils/date";
import dayjs from "dayjs";

moment.locale("it", {
  months:
    "Gennaio_Febbraio_Marzo_Aprile_Maggio_Giugno_Luglio_Agosto_Settembre_Ottobre_Novembre_Dicembre".split(
      "_"
    ),
  monthsShort:
    "Gen._Feb._Mar._Apr._Mag._Giu._Lug._Ago._Set._Ott._Nov._Dic.".split("_"),
  monthsParseExact: true,
  weekdays: "Domenica_Lunedì_Martedì_Mercoledì_Giovedì_Venerdì_Sabato".split(
    "_"
  ),
  weekdaysShort: "Dom._Lun._Mar._Mer._Gio._Ven._Sab.".split("_"),
  weekdaysMin: "Do._Lu._Ma._Me._Gi._Ve._Sa.".split("_"),
  weekdaysParseExact: true,
});
const localizer = momentLocalizer(moment);

export const getEventTitle = (activity: Activity) => {
  let title = "";

  switch (activity.type) {
    case "online-traning":
      title = activity.eventMeta.video?.name;
      break;
    case "long-live-traning":
      title = "Allenamento con supervisione";
      break;
    default:
  }

  return title;
};

export const getEventPlayedSeconds = (activity: Activity): number => {
  let duration = 0;

  switch (activity.type) {
    case "online-traning":
      duration = activity.eventMeta.log?.playedSeconds || 0;
      break;
    default:
  }

  return duration;
};

export const getReservationTitle = (reservation: Reservation) => {
  let title = "";

  switch (reservation.type) {
    case "calendly.event_scheduled":
      title = reservation.eventMeta.name;
      break;
    default:
  }

  return title;
};

const CalendarToolbar: React.FC<any> = (toolbarProps) => {
  const goToBack = () => {
    toolbarProps.date.setMonth(toolbarProps.date.getMonth() - 1);
    toolbarProps.onNavigate("prev");
  };

  const goToNext = () => {
    toolbarProps.date.setMonth(toolbarProps.date.getMonth() + 1);
    toolbarProps.onNavigate("next");
  };

  const goToCurrent = () => {
    const now = new Date();
    toolbarProps.date.setMonth(now.getMonth());
    toolbarProps.date.setYear(now.getFullYear());
    toolbarProps.onNavigate("current");
  };

  const label = () => {
    const date = moment(toolbarProps.date);
    return (
      <span>
        <b>{date.format("MMMM")}</b>
        <span> {date.format("YYYY")}</span>
      </span>
    );
  };
  return (
    <HStack justifyContent="space-between" mb="1rem">
      <label>{label()}</label>

      <HStack>
        {toolbarProps.view === "day" && (
          <Button variant="ghost" onClick={() => toolbarProps.onView("month")}>
            Torna al calendario
          </Button>
        )}
        <CalendarNavButtons
          goBack={goToBack}
          goNext={goToNext}
          goToday={goToCurrent}
        />
      </HStack>
    </HStack>
  );
};

const Board: React.FC = observer(() => {
  const { session } = useStores();
  const { t } = useTranslation();

  const [dateInfoLocal, setDateInfoLocal] = useState<{
    start: Date;
    end: Date;
  }>({
    start: moment().startOf("month").subtract(7, "day").toDate(),
    end: moment().endOf("month").add(7, "day").toDate(),
  });
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isPlanningOpen,
    onClose: onPlanningClose,
    onOpen: onPlanningOpen,
  } = useDisclosure();
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [activePlanning, setActivePlanning] =
    useState<Partial<Planning> | null>(null);

  useEffect(() => {
    if (dateInfoLocal) {
      session.fetchActivities(dateInfoLocal.start, dateInfoLocal.end);
      session.fetchCalendarReservations(dateInfoLocal.start, dateInfoLocal.end);
      session.fetchPlanning(dateInfoLocal.start, dateInfoLocal.end);
    }
  }, [dateInfoLocal, session]);

  const handleReload = useCallback(() => {
    if (dateInfoLocal) {
      session.fetchActivities(dateInfoLocal.start, dateInfoLocal.end);
      session.fetchCalendarReservations(dateInfoLocal.start, dateInfoLocal.end);
      session.fetchPlanning(dateInfoLocal.start, dateInfoLocal.end);
    }
  }, [dateInfoLocal, session]);

  const handleClickEvent = useCallback(
    (event: any) => {
      if (
        event.extendedProps.type === "reservation" &&
        !session.isTrialExpired &&
        !session.isSubscriptionDeactivated
      ) {
        const selectedReservation = session.calendarReservations.find(
          (reservation) => reservation.id === event.id
        );

        if (selectedReservation) {
          setSelectedReservation(selectedReservation);
          onOpen();
        }
      }
    },
    [onOpen, session]
  );

  const handleDeleteReservation = useCallback(
    (reservation: Reservation) => {
      // Open Calendaly to delete the event.
      const cancelURL =
        reservation.eventMeta.cancel_url ||
        // Calendly cancel link = calendly invitee ID (get from uri "https://api.calendly.com/scheduled_events/HIFNXPLSXFK54GCS/invitees/FMLDPI42MCWLKHCV" => FMLDPI42MCWLKHCV )
        `https://calendly.com/cancellations/${reservation.eventMeta.invitee.uri
          .split("/")
          .pop()}`;

      window.open(cancelURL, "_blank");

      setSelectedReservation(null);
      handleReload();
    },
    [handleReload]
  );

  const handleRescheduleReservation = useCallback(
    (reservation: Reservation) => {
      const rescheduleURL = reservation.eventMeta.reschedule_url;
      if (rescheduleURL) {
        window.open(rescheduleURL, "_blank");
      }
      setSelectedReservation(null);
    },
    []
  );

  const handleDateRangeChange = useCallback((dateInfo) => {
    setDateInfoLocal(dateInfo);
  }, []);

  // https://mostaza.atlassian.net/wiki/spaces/SAFW/pages/1804861441/SO4574+-+Sessioni+Personal+Trainer#Calendario-degli-allenamenti
  const dayPropGetter = useCallback(
    (date: Date) => {
      // salmone #E25C89
      // viola #902C8E

      const defaultStyle = { cursor: "pointer" };

      const dailyPlanning = session.plannings.find((planning) =>
        datesAreOnSameDay(planning.dateTime, date)
      );

      return {
        style: {
          ...defaultStyle,
          ...(dailyPlanning &&
            dailyPlanning.type === PlanningType.LIVE_WORKOUT && {
              borderColor: "#E25C89",
              outline: "2px solid #E25C89",
              outlineOffset: "-2px",
            }),
          ...(dailyPlanning &&
            dailyPlanning.type === PlanningType.RECORDED_WORKOUT && {
              borderColor: "#902C8E",
              outline: "2px solid #902C8E",
              outlineOffset: "-2px",
            }),
        },
      };
    },
    [session.plannings]
  );

  const eventPropGetter = useCallback((event: any) => {
    return {
      style: {
        ...(event.extendedProps?.category === "online-traning" && {
          backgroundColor: "#902C8E",
        }),
        ...(event.extendedProps?.category === "long-live-traning" && {
          backgroundColor: "#E25C89",
        }),
      },
    };
  }, []);

  const handleSelectSlot = useCallback(
    (slotInfo) => {
      // Check if already exists a planning for slotInfo.start

      if (dayjs(slotInfo.start).isAfter(dayjs())) {
        const dailyPlanning = session.plannings.find((planning) =>
          datesAreOnSameDay(planning.dateTime, slotInfo.start)
        );

        // IF exists -> Edit planning
        if (dailyPlanning) {
          setActivePlanning(dailyPlanning);
          onPlanningOpen();
        } else {
          // ELSE -> Add new planning
          setActivePlanning({ dateTime: slotInfo.start });
          onPlanningOpen();
        }
      }
    },
    [onPlanningOpen, session.plannings]
  );

  const handleSubmitPlanning = useCallback(
    async (planning: Partial<Planning>) => {
      if (planning.id && planning.type === PlanningType.NO_WORKOUT) {
        // delete
        await session.deletePlanning(planning.id);
      } else if (planning.id) {
        // update
        await session.updatePlanning(planning);
      } else {
        // add
        await session.addPlanning(planning);
      }
      onPlanningClose();
    },
    [onPlanningClose, session]
  );

  return (
    <Box p={30} w={"100%"}>
      {session.user && (
        <>
          <TrialBanner
            isTrialMode={session.isTrialMode}
            signUpDate={session.user.createdAt}
            trialPeriod={7}
            showLink
          />
          <SubscriptionBanner
            isSubscriptionDeactivated={session.isSubscriptionDeactivated}
            showLink
          />
        </>
      )}
      <div style={{ width: "100%", height: "600px" }}>
        <Calendar
          defaultView={"month"}
          events={session.calendarEvents}
          culture={"it"}
          localizer={localizer}
          onRangeChange={handleDateRangeChange}
          components={{ toolbar: CalendarToolbar }}
          onSelectEvent={handleClickEvent}
          messages={{ showMore: (total) => `+${total} attività` }}
          dayPropGetter={dayPropGetter}
          eventPropGetter={eventPropGetter}
          // https://github.com/jquense/react-big-calendar/blob/master/stories/props/selectable.mdx
          selectable="ignoreEvents"
          onSelectSlot={handleSelectSlot}
        />
      </div>

      {selectedReservation && (
        <ReservationModal
          isOpen={isOpen}
          onClose={onClose}
          reservation={selectedReservation}
          onCancel={handleDeleteReservation}
          onReschedule={handleRescheduleReservation}
        />
      )}

      <PlanningModal
        isOpen={isPlanningOpen}
        onClose={onPlanningClose}
        planning={activePlanning}
        onSubmit={handleSubmitPlanning}
      />
    </Box>
  );
});

export default styled(Board)`
  .fc .fc-col-header-cell-cushion {
    display: inline-block;
    padding: 2px 4px;
  }
  .fc-theme-standard td,
  .fc-theme-standard th {
    background-image: linear-gradient(
      to right,
      #ccc 0%,
      #ccc 50%,
      transparent 50%
    );
    background-size: 12px 1px;
    background-repeat: repeat-x;
    border: 0px solid !important;
  }
  td {
    border: 0px solid !important;
  }
  .fc-button-primary {
    background-color: #872996 !important;
  }
`;
